import { DataTable } from "@/components/custom/data-table";
import { Box, Stack, styled, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useProductCategories } from "@/hooks/use-products-categories";
import { useProductCategoryFeesByCategoryId } from "@/hooks/use-product-category-fee-by-category-id";
import { CustomButton } from "@/components/custom/custom-button";
import { getProductFeeColumns } from "./_components/product-fee-column";
import {
  createProductCategoryFee,
  updateProductCategoryFee,
} from "@/services/pricing-config.service";
import ProductCategoryFeeDialog from "./_components/product-category-fee-dialog";
import CustomPagination from "@/components/custom/custom-pagination";
import { useSearchParams } from "react-router-dom";

const RootWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const CategoryTabs = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : theme.palette.grey[100],
  borderRadius: 20,
  padding: 4,
  width: "fit-content",
  gap: 2,
}));

const CategoryBtn = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ theme, isActive }) => ({
  minWidth: 48,
  height: 32,
  padding: "0 14px",
  borderRadius: 20,
  fontSize: "0.8rem",
  fontWeight: isActive ? 600 : 400,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  backgroundColor: isActive ? theme.palette.primary.main : "transparent",
  color: isActive ? "#fff" : theme.palette.text.secondary,
  transition: "all 150ms ease",
  "&:hover": {
    backgroundColor: isActive
      ? theme.palette.primary.main
      : theme.palette.action.hover,
    color: isActive ? "#fff" : theme.palette.text.primary,
  },
}));

const FeeConfiguration = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [newFeeOpen, setNewFeeOpen] = useState(false);
  const [editFee, setEditFee] = useState(null);

  const [searchParams] = useSearchParams();
  const searchValue = searchParams.get("search") || "";
  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);

  const queryParams = {
    page: currentPage,
    size: pageSize,
    search: searchValue,
  };

  const { data } = useProductCategories(true);

  const categories = data?.data?.data ?? [];

  useEffect(() => {
    if (categories.length > 0 && activeCategory === null) {
      setActiveCategory(categories[0].id);
    }
  }, [categories]);

  const {
    data: dataByCategoryId,
    isLoading: isLoadingByCategoryId,
    error,
  } = useProductCategoryFeesByCategoryId(activeCategory, queryParams);

  const tableData = dataByCategoryId?.data?.data ?? [];

  const handleEditClick = (fee) => setEditFee(fee);
  const handleEditClose = () => setEditFee(null);

  return (
    <RootWrapper>
      <Card>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography fontWeight={500} fontSize={18}>
            Product Fee for Categories
          </Typography>

          <Stack direction={"row"} spacing={1} alignItems="center">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Category
              </Typography>
              <CategoryTabs>
                {categories.map((cat) => (
                  <CategoryBtn
                    key={cat.id}
                    isActive={activeCategory === cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                  >
                    {cat.code}
                  </CategoryBtn>
                ))}
              </CategoryTabs>
            </Box>
            <CustomButton
              variantType="outline"
              onClick={() => setNewFeeOpen(true)}
              sx={{ height: 35 }}
            >
              New Fee
            </CustomButton>
          </Stack>
        </Stack>

        <DataTable
          data={tableData}
          columns={getProductFeeColumns(handleEditClick)}
          isLoading={isLoadingByCategoryId}
          isError={!!error}
        />

        <CustomPagination
          total={dataByCategoryId?.data?.totalCount ?? 0}
          perPageOptions={[5, 10, 20, 50, 100]}
        />
      </Card>

      <ProductCategoryFeeDialog
        open={newFeeOpen}
        onClose={() => setNewFeeOpen(false)}
        mutationFn={createProductCategoryFee}
      />

      <ProductCategoryFeeDialog
        open={Boolean(editFee)}
        onClose={handleEditClose}
        categoryFee={editFee}
        mutationFn={(payload) =>
        updateProductCategoryFee({ id: editFee?.id, ...payload })
        }
      />
    </RootWrapper>
  );
};

export default FeeConfiguration;
